import type { OutputLayer } from '@/components/preview/output-layer-preview';
import type { PreviewLayer } from '@/components/preview/preview-static-layer-renderer';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import type { OutputPreviewTab, SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';
import { applySequencedPlaybackToPreviewLayers } from '@/helpers/preview/preview-sequenced-playback.helper';
import { resolveOutputPreviewLayers } from '@/helpers/resolve-output-preview-layers';

type SequencedPlaybackSettings = {
  loopEnabled: boolean;
  dwellMs?: number;
};

const SEQUENCED_LAYER_COMPONENTS = new Set(['imageSequence', 'presentationSlides']);

function isSequencedLayer(layer: OutputLayer): boolean {
  return SEQUENCED_LAYER_COMPONENTS.has(layer.componentId);
}

function collectSequencedPlaybackFromLayers(layers: OutputLayer[], target: Map<string, SequencedPlaybackSettings>) {
  for (const layer of layers) {
    if (!isSequencedLayer(layer) || !layer.itemId) {
      continue;
    }
    if (layer.data?.autoLoop === 'true') {
      target.set(layer.itemId, {
        loopEnabled: true,
        dwellMs: layer.data.dwellMs ? Number.parseInt(layer.data.dwellMs, 10) : undefined,
      });
      continue;
    }
    if (layer.data?.autoAdvance === 'true') {
      target.set(layer.itemId, {
        loopEnabled: false,
        dwellMs: layer.data.dwellMs ? Number.parseInt(layer.data.dwellMs, 10) : undefined,
      });
    }
  }
}

function collectSequencedPlaybackFromCueRows(
  rows: SimulationCueSheetRow[],
  target: Map<string, SequencedPlaybackSettings>,
) {
  for (const row of rows) {
    if (!row.advanceSettings || !row.fileType) {
      continue;
    }
    if (row.fileType !== 'imageSequence' && row.fileType !== 'pdf' && row.fileType !== 'presentation') {
      continue;
    }
    target.set(row.itemId, {
      loopEnabled: row.advanceSettings.loopEnabled,
      dwellMs: row.advanceSettings.dwellMs,
    });
  }
}

/** API preview layers omit sequenced playback metadata; merge from playState and cue sheet. */
export function enrichLivePreviewLayersWithSequencedPlayback(
  layers: OutputLayer[],
  options: {
    liveContextState?: LiveContextStateDto;
    tab: OutputPreviewTab;
    simulationRows?: SimulationCueSheetRow[];
  },
): OutputLayer[] {
  const settingsByItemId = new Map<string, SequencedPlaybackSettings>();

  const playStateLayers = resolveOutputPreviewLayers({
    performMode: 'play',
    tab: options.tab,
    liveContextState: options.liveContextState,
  });
  collectSequencedPlaybackFromLayers(playStateLayers, settingsByItemId);
  collectSequencedPlaybackFromCueRows(options.simulationRows ?? [], settingsByItemId);

  const currentItemId = options.liveContextState?.cueState?.currentStep?.itemId;
  const currentRefSettings = options.liveContextState?.cueState?.currentRefSettings;
  const currentFileType = options.simulationRows?.find((r) => r.itemId === currentItemId)?.fileType;
  if (currentItemId && currentRefSettings) {
    const isSequencedFileType =
      currentFileType === 'imageSequence' || currentFileType === 'pdf' || currentFileType === 'presentation';
    if (isSequencedFileType) {
      const existing = settingsByItemId.get(currentItemId);
      settingsByItemId.set(currentItemId, {
        loopEnabled: currentRefSettings.loopEnabled,
        dwellMs: existing?.dwellMs,
      });
    }
  }

  if (settingsByItemId.size === 0) {
    return layers;
  }

  return layers.map((layer) => {
    if (!isSequencedLayer(layer) || !layer.itemId) {
      return layer;
    }
    const settings = settingsByItemId.get(layer.itemId);
    if (!settings) {
      return layer;
    }
    const [enriched] = applySequencedPlaybackToPreviewLayers([layer as PreviewLayer], layer.itemId, settings);
    return (enriched ?? layer) as OutputLayer;
  });
}
